// Identifies a video container from its leading bytes.
//
// Filenames and browser-reported MIME types both lie: users rename .mov to .mp4,
// and some browsers report an empty type for QuickTime. The bytes do not lie, so
// this is the authority when labelling an upload's content type.
//
// Detection is deliberately conservative — an unrecognised header returns null so
// callers fall back to the filename rather than acting on a guess. That matters
// most for the storage backfill (functions/scripts/migrate-video-content-types.js),
// which rewrites metadata on real user data and must never "correct" a file it
// does not actually recognise. Keep that script's table in sync with this one.

/** Bytes needed at the head of a file for detection. */
export const VIDEO_SNIFF_BYTE_COUNT = 64;

const readAscii = (bytes: Uint8Array, start: number, length: number): string => {
  if (start + length > bytes.length) return '';

  let out = '';
  for (let i = start; i < start + length; i += 1) {
    out += String.fromCharCode(bytes[i]);
  }
  return out;
};

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean => (
  signature.length <= bytes.length && signature.every((byte, index) => bytes[index] === byte)
);

/**
 * ISO base media files (mp4, mov, 3gp, m4v) all carry an `ftyp` box; the major
 * brand that follows says which flavour it is.
 */
const sniffIsoBaseMedia = (bytes: Uint8Array): string | null => {
  if (readAscii(bytes, 4, 4) !== 'ftyp') return null;

  const brand = readAscii(bytes, 8, 4).trim().toLowerCase();

  if (brand === 'qt') return 'video/quicktime';
  // Brands are versioned: 3gp4/3gp5 for 3GPP, 3g2a/3g2b for 3GPP2.
  if (brand.startsWith('3g2')) return 'video/3gpp2';
  if (brand.startsWith('3g')) return 'video/3gpp';
  // Audio-only MPEG-4 shares the container; not our business to relabel.
  if (brand === 'm4a' || brand === 'm4b' || brand === 'm4p') return null;

  return 'video/mp4';
};

/**
 * Matroska and WebM share the EBML header; the DocType string distinguishes them
 * and sits within the first few dozen bytes.
 */
const sniffMatroska = (bytes: Uint8Array): string | null => {
  if (!startsWithBytes(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return null;

  const header = readAscii(bytes, 0, bytes.length);
  if (header.includes('webm')) return 'video/webm';
  if (header.includes('matroska')) return 'video/x-matroska';

  return null;
};

/**
 * Best-effort container detection. Returns null when the header is unrecognised,
 * truncated, or ambiguous — never a guess.
 */
export const sniffVideoContentType = (bytes: Uint8Array): string | null => {
  if (bytes.length < 12) return null;

  const isoType = sniffIsoBaseMedia(bytes);
  if (isoType) return isoType;

  const matroskaType = sniffMatroska(bytes);
  if (matroskaType) return matroskaType;

  if (readAscii(bytes, 0, 4) === 'OggS') return 'video/ogg';

  if (readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'AVI ') {
    return 'video/x-msvideo';
  }

  // ASF header GUID — the container behind .wmv.
  if (startsWithBytes(bytes, [0x30, 0x26, 0xb2, 0x75, 0x8e, 0x66, 0xcf, 0x11])) {
    return 'video/x-ms-wmv';
  }

  if (readAscii(bytes, 0, 3) === 'FLV' && bytes[3] === 0x01) return 'video/x-flv';

  // MPEG program stream (0x1BA) and elementary video sequence (0x1B3).
  if (startsWithBytes(bytes, [0x00, 0x00, 0x01, 0xba])) return 'video/mpeg';
  if (startsWithBytes(bytes, [0x00, 0x00, 0x01, 0xb3])) return 'video/mpeg';

  // MPEG transport streams start with a 0x47 sync byte, but a single 0x47 is far
  // too weak a signal to act on, so .ts files are left alone.
  return null;
};
